/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { FC } from 'react';
import cx from 'classnames';
import { styled } from '@linaria/react';
import { sendGTM, GTMEventKey, CustomEventName, GTMConstants } from '@fe-cnyes/fe-common-gtm';

const DEFAULT_EMPTY_CHART = '/assets/icons/win-indicator/empty.svg';

const WININDICATOR_LINK = 'https://events.cnyes.com/anuewin-25942';

type WinIndicatorProps = {
  title: string;
  direction: 0 | 1 | 2;
  children: React.ReactNode;
};

type WinIndicatorStockProps = {
  id: number;
  state: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  stockName: string;
  percentage: number;
  direction: 0 | 1 | 2;
};

enum WinIndicatorState {
  CORRECTION = 'correction',
  LONG = 'long',
  SHORT_TO_LONG = 'short-to-long',
  LONG_CORRECTION = 'correction-long',
  SHORT = 'short',
  LONG_TO_SHORT = 'long-to-short',
  SHORT_CORRECTION = 'correction-short',
}

type KEY = keyof typeof WinIndicatorState;

const WIN_INDICATOR_STATE_ENUM = [
  'CORRECTION',
  'LONG',
  'SHORT_TO_LONG',
  'LONG_CORRECTION',
  'SHORT',
  'LONG_TO_SHORT',
  'SHORT_CORRECTION',
];

const WIN_INDICATOR_SVG = {
  [WinIndicatorState.CORRECTION]:
    '/assets/icons/win-indicator/correction.svg',
  [WinIndicatorState.LONG]: '/assets/icons/win-indicator/long.svg',
  [WinIndicatorState.LONG_TO_SHORT]:
    '/assets/icons/win-indicator/long-to-short.svg',
  [WinIndicatorState.LONG_CORRECTION]:
    '/assets/icons/win-indicator/correction-long.svg',
  [WinIndicatorState.SHORT]: '/assets/icons/win-indicator/short.svg',
  [WinIndicatorState.SHORT_TO_LONG]:
    '/assets/icons/win-indicator/short-to-long.svg',
  [WinIndicatorState.SHORT_CORRECTION]:
    '/assets/icons/win-indicator/correction-short.svg',
};

const WIN_INDICATOR_SPAN = {
  [WinIndicatorState.CORRECTION]: '盤整',
  [WinIndicatorState.LONG]: '偏強',
  [WinIndicatorState.SHORT_TO_LONG]: '中弱短強',
  [WinIndicatorState.LONG_CORRECTION]: '強勢盤整',
  [WinIndicatorState.SHORT]: '偏弱',
  [WinIndicatorState.LONG_TO_SHORT]: '中強短弱',
  [WinIndicatorState.SHORT_CORRECTION]: '弱勢盤整',
};

const WIN_INDICATOR_STRG_STATE = ['correction', 'long', 'short'];

/**
 * 檢查傳入參數是否為數字
 *
 * @param {*} x - 任意值
 * @returns boolean
 */
const isNumber = (x: any) => {
  return !Number.isNaN(x) && typeof x === 'number';
};

/**
 * 四捨五入
 * 23445.567 -> 23445.57
 *
 * @param {number} val - 傳入數字
 * @param {number} [precision=2] - 四捨五入小數位數
 * @returns {string} 傳入數值四捨五入後的數字
 */
const roundX = (val: any, precision = 2) => {
  if (!isNumber(val)) return NaN;

  return (
    Math.round(Math.round(val * Math.pow(10, (precision || 0) + 1)) / 10) /
    Math.pow(10, precision || 0)
  );
};

const Container = styled.div`
  border-radius: 6px;
  border: solid 1px #e2e8f1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 16px;

  &.correction,
  &.long {
    background-image: linear-gradient(to bottom, #fff9f7, #fff 100%);
  }

  &.short {
    background-image: linear-gradient(to bottom, #f9fff7, #fff 100%);
  }
`;

const Percentage = styled.p`
  font-size: 18px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: 0.5px;
  color: #3d91ff;
  margin-bottom: 8px;
`;

const CardTitle = styled.p`
  text-align: center;
  letter-spacing: 1px;
  padding: 14px 24px;
  font-size: 18px;
  font-weight: 600;
  font-stretch: normal;
  flex-wrap: nowrap;
  white-space: nowrap;

  &.correction,
  &.long {
    color: #e03f19;
  }

  &.short {
    color: #208306;
  }
`;

const Chart = styled.div`
  border-radius: 7.7px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;

  &.correction {
    box-shadow: 0 0 6px 0 rgba(81, 92, 105, 0.3);
    border: solid 1px rgba(81, 92, 105, 0.5);
  }

  &.long,
  &.short-to-long,
  &.correction-long {
    box-shadow: 0 0 6px 0 rgba(224, 63, 25, 0.3);
    border: solid 1px rgba(224, 63, 25, 0.5);
  }

  &.short,
  &.long-to-short,
  &.correction-short {
    box-shadow: 0 0 6px 0 rgba(32, 131, 6, 0.3);
    border: solid 1px rgba(32, 131, 6, 0.5);
  }

  img {
    width: 64px;
    height: 64px;
    padding: 5px;
    border-radius: 6px;
  }
`;

const StockName = styled.h6`
  margin: 12px 0 16px;
  font-weight: 400;
  font-size: 16px;
  line-height: 1;
  color: black;
`;

const Description = styled.span`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #848d97;
`;

const StockContainer = styled.a`
  text-align: center;
`;

const WinIndicatorChildrenContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 0 16px;
`;

export const WinIndicatorContainer = ({
  title,
  direction,
  children,
}: WinIndicatorProps) => {
  return (
    <Container className={cx(WIN_INDICATOR_STRG_STATE[direction])}>
      <CardTitle className={cx(WIN_INDICATOR_STRG_STATE[direction])}>
        #{title}
      </CardTitle>
      <WinIndicatorChildrenContainer>{children}</WinIndicatorChildrenContainer>
    </Container>
  );
};

export const WinIndicatorStock: FC<WinIndicatorStockProps> = ({
  id,
  stockName,
  percentage,
  state,
  direction,
}) => {
  const onClick = () => {
    sendGTM({
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TOPIC,
      [GTMEventKey.SECTION]: `${GTMConstants.SECTION_PLACEHOLDER}_贏指標`,
      [GTMEventKey.CLICK_ITEM]: `${id}_${stockName}`
    })
  };
  return (
    <StockContainer href={WININDICATOR_LINK} onClick={onClick}>
      <Chart className={cx(WIN_INDICATOR_STRG_STATE[direction])}>
        <img
          src={
            WIN_INDICATOR_SVG[
              WinIndicatorState[WIN_INDICATOR_STATE_ENUM[state] as KEY]
            ] || DEFAULT_EMPTY_CHART
          }
          alt={
            WIN_INDICATOR_SPAN[
              WinIndicatorState[WIN_INDICATOR_STATE_ENUM[state] as KEY]
            ]
          }
        />
      </Chart>
      <StockName>{stockName}</StockName>
      <Percentage>{roundX(percentage)}%</Percentage>
      <Description>勝率</Description>
    </StockContainer>
  );
};

export default WinIndicatorContainer;
